import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { ScrollArea } from '../ui/scroll-area';
import { Checkbox } from '../ui/checkbox';
import { 
  CheckCircle, 
  XCircle, 
  Lightbulb, 
  Target, 
  Clock, 
  Award,
  Mic,
  MessageSquare,
  AlertTriangle,
  Sparkles,
  ChevronRight
} from 'lucide-react';

/**
 * SessionGuidanceModal - Reusable pre-session guidance popup
 * Shows do's, don'ts, tips, and scoring criteria before starting any training session
 * 
 * Usage:
 * <SessionGuidanceModal
 *   open={showGuide}
 *   onClose={() => setShowGuide(false)}
 *   onStart={handleStartSession}
 *   phase="phase5_job_analysis"
 *   customContent={optionalCustomContent}
 * />
 */

// Pre-defined guidance content for each phase/session type
const GUIDANCE_CONTENT = {
  // Phase 2: Recruiter Simulations
  phase2_recruiter_sim: {
    title: "Recruiter Simulation",
    subtitle: "Practice real recruiter conversations",
    icon: MessageSquare,
    color: "from-blue-500 to-cyan-600",
    duration: "10-15 minutes",
    scoringCriteria: [
      "Communication clarity and professionalism",
      "Active listening and response relevance",
      "Problem-solving approach",
      "Confidence and rapport building"
    ],
    dos: [
      "Speak naturally as if talking to a real person",
      "Ask clarifying questions when needed",
      "Use professional but friendly tone",
      "Take brief pauses to think before responding"
    ],
    donts: [
      "Don't read from scripts or external sources",
      "Don't rush through responses",
      "Don't use filler words excessively",
      "Don't forget to address all points raised"
    ],
    tips: [
      "Imagine you're on a real phone call",
      "Your voice tone matters as much as your words",
      "It's okay to ask the AI to repeat if needed"
    ]
  },

  // Phase 4: Final Exam
  phase4_final_exam: {
    title: "Final Assessment",
    subtitle: "Test your knowledge from all topics",
    icon: Award,
    color: "from-amber-500 to-orange-600",
    duration: "30-45 minutes",
    scoringCriteria: [
      "Knowledge accuracy (40%)",
      "Response completeness (30%)",
      "Practical application (20%)",
      "Communication quality (10%)"
    ],
    dos: [
      "Read each question carefully before answering",
      "Take your time - there's no rush",
      "Answer all parts of multi-part questions",
      "Review your answers before submitting"
    ],
    donts: [
      "Don't leave any questions unanswered",
      "Don't second-guess yourself too much",
      "Don't look up answers externally",
      "Don't skip the review step"
    ],
    tips: [
      "You need 85% to pass",
      "You can retake if needed, but aim for first-attempt success",
      "Focus on understanding, not memorization"
    ]
  },

  // Phase 5: Job Analysis
  phase5_job_analysis: {
    title: "Job Analysis Simulation",
    subtitle: "Understand the role requirements deeply",
    icon: Target,
    color: "from-violet-500 to-purple-600",
    duration: "5-10 minutes",
    scoringCriteria: [
      "Understanding of job requirements",
      "Relevant questions asked",
      "Identification of key skills needed",
      "Communication with hiring manager"
    ],
    dos: [
      "Ask about specific technical requirements",
      "Clarify team structure and reporting",
      "Understand the 'must-have' vs 'nice-to-have' skills",
      "Take notes mentally for the screening phase"
    ],
    donts: [
      "Don't make assumptions about the role",
      "Don't skip clarifying questions",
      "Don't focus only on technical skills - soft skills matter",
      "Don't forget to ask about timeline and urgency"
    ],
    tips: [
      "The job description will be visible throughout",
      "This information helps you in the next phases",
      "Voice-only mode: speak clearly into your microphone"
    ]
  },

  // Phase 5: Candidate Screening
  phase5_screening: {
    title: "Candidate Screening",
    subtitle: "Conduct a phone screening interview",
    icon: Mic,
    color: "from-emerald-500 to-teal-600",
    duration: "10-15 minutes",
    scoringCriteria: [
      "Question quality and relevance",
      "Listening and follow-up skills",
      "Technical assessment accuracy",
      "Professional demeanor"
    ],
    dos: [
      "Start with rapport-building questions",
      "Ask open-ended questions to learn more",
      "Probe deeper on interesting answers",
      "Assess both technical skills and cultural fit"
    ],
    donts: [
      "Don't ask yes/no questions only",
      "Don't interrupt the candidate",
      "Don't make snap judgments",
      "Don't forget to sell the opportunity"
    ],
    tips: [
      "The candidate's profile is visible for reference",
      "Listen for red flags AND green flags",
      "Your assessment leads to the final verdict"
    ]
  },

  // Phase 5: Final Assessment/Verdict
  phase5_final_assessment: {
    title: "Final Assessment",
    subtitle: "Make your hiring recommendation",
    icon: Award,
    color: "from-rose-500 to-pink-600",
    duration: "Up to 10 exchanges",
    scoringCriteria: [
      "Verdict accuracy",
      "Justification quality",
      "Evidence-based reasoning",
      "Risk assessment"
    ],
    dos: [
      "Support your verdict with specific evidence",
      "Consider both strengths and concerns",
      "Be decisive - make a clear recommendation",
      "Explain your reasoning thoroughly"
    ],
    donts: [
      "Don't be wishy-washy - commit to a verdict",
      "Don't ignore red flags from screening",
      "Don't forget to mention concerns even if recommending",
      "Don't make it personal - stay professional"
    ],
    tips: [
      "Submit your verdict when you are ready (up to 10 exchanges)",
      "Score 80% or higher to pass and advance",
      "The hiring manager will challenge your reasoning"
    ]
  },

  // Phase 6: Sourcing Labs
  phase6_sourcing: {
    title: "Sourcing Lab",
    subtitle: "Find and engage candidates",
    icon: Target,
    color: "from-amber-500 to-orange-600",
    duration: "15-20 minutes",
    scoringCriteria: [
      "Search strategy effectiveness",
      "Candidate quality",
      "Outreach message quality",
      "Boolean search skills"
    ],
    dos: [
      "Use Boolean operators effectively",
      "Think creatively about where to find talent",
      "Personalize your outreach messages",
      "Document your search strategy"
    ],
    donts: [
      "Don't use generic templates",
      "Don't spam candidates",
      "Don't ignore passive candidates",
      "Don't limit yourself to one platform"
    ],
    tips: [
      "Quality over quantity in candidate selection",
      "Your outreach message is your first impression",
      "Think like a candidate - what would attract you?"
    ]
  },

  // Phase 7: Role Play Lab
  phase7_roleplay: {
    title: "Role Play Scenario",
    subtitle: "Practice real-world conversations",
    icon: MessageSquare,
    color: "from-indigo-500 to-violet-600",
    duration: "10-15 minutes",
    scoringCriteria: [
      "Communication effectiveness",
      "Handling objections",
      "Building rapport",
      "Achieving conversation goals"
    ],
    dos: [
      "Stay in character throughout",
      "Listen actively and respond appropriately",
      "Handle objections professionally",
      "Work toward the conversation goal"
    ],
    donts: [
      "Don't break character",
      "Don't get defensive with objections",
      "Don't dominate the conversation",
      "Don't forget your objective"
    ],
    tips: [
      "Each scenario has a specific goal",
      "The AI persona will challenge you realistically",
      "Reflect on feedback for improvement"
    ]
  },

  // Phase 8: Final Certification
  phase8_final: {
    title: "Final Certification Assessment",
    subtitle: "Demonstrate your mastery",
    icon: Award,
    color: "from-gradient-500 to-yellow-600",
    duration: "45-60 minutes",
    scoringCriteria: [
      "Comprehensive knowledge (30%)",
      "Practical application (30%)",
      "Communication skills (25%)",
      "Problem-solving (15%)"
    ],
    dos: [
      "Showcase everything you've learned",
      "Apply knowledge to realistic scenarios",
      "Communicate clearly and confidently",
      "Demonstrate your unique approach"
    ],
    donts: [
      "Don't rush - this is your final showcase",
      "Don't second-guess your training",
      "Don't forget the fundamentals",
      "Don't panic - you've prepared for this"
    ],
    tips: [
      "This is your moment to shine",
      "Trust your training and experience",
      "Upon passing, you'll receive your certification"
    ]
  }
};

const SessionGuidanceModal = ({ 
  open, 
  onClose, 
  onStart, 
  phase,
  customContent = null,
  showDontShowAgain = true 
}) => {
  const [dontShowAgain, setDontShowAgain] = useState(false);
  const [step, setStep] = useState(0); // 0: overview, 1: dos/donts, 2: tips
  
  // Get content for this phase
  const content = customContent || GUIDANCE_CONTENT[phase] || GUIDANCE_CONTENT.phase5_job_analysis;
  const Icon = content.icon || Target;

  // Check localStorage for "don't show again" preference
  useEffect(() => {
    if (open) {
      const key = `guidance_skip_${phase}`;
      const skipGuide = localStorage.getItem(key);
      if (skipGuide === 'true' && showDontShowAgain) {
        // Auto-start if user chose not to show again
        onStart?.();
        onClose?.();
      }
    }
  }, [open, phase, onStart, onClose, showDontShowAgain]);

  const handleStart = () => {
    if (dontShowAgain) {
      localStorage.setItem(`guidance_skip_${phase}`, 'true');
    }
    onStart?.();
    onClose?.();
  };

  const totalSteps = 3;

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose?.()}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-hidden">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className={`p-3 rounded-xl bg-gradient-to-br ${content.color} text-white`}>
              <Icon className="h-6 w-6" />
            </div>
            <div>
              <DialogTitle className="text-xl">{content.title}</DialogTitle>
              <DialogDescription>{content.subtitle}</DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className="py-4">
          {/* Progress dots */}
          <div className="flex justify-center gap-2 mb-4">
            {[0, 1, 2].map(i => (
              <button
                key={i}
                onClick={() => setStep(i)}
                className={`w-2 h-2 rounded-full transition-all ${
                  step === i ? 'w-6 bg-primary' : 'bg-muted-foreground/30'
                }`}
              />
            ))}
          </div>

          <ScrollArea className="h-[300px] pr-4">
            {step === 0 && (
              <div className="space-y-4">
                {/* Overview */}
                <div className="flex items-center gap-4 p-3 rounded-lg bg-muted/50">
                  <Clock className="h-5 w-5 text-muted-foreground" />
                  <div>
                    <p className="font-medium">Duration</p>
                    <p className="text-sm text-muted-foreground">{content.duration}</p>
                  </div>
                </div>

                {/* Scoring Criteria */}
                <div>
                  <h4 className="font-medium flex items-center gap-2 mb-2">
                    <Target className="h-4 w-4 text-primary" />
                    What You'll Be Scored On
                  </h4>
                  <div className="space-y-2">
                    {content.scoringCriteria.map((criteria, i) => (
                      <div key={i} className="flex items-start gap-2 text-sm">
                        <Badge variant="outline" className="w-6 h-6 p-0 flex items-center justify-center rounded-full text-xs">
                          {i + 1}
                        </Badge>
                        <span>{criteria}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {step === 1 && (
              <div className="space-y-4">
                {/* Do's */}
                <div>
                  <h4 className="font-medium flex items-center gap-2 mb-3 text-emerald-600">
                    <CheckCircle className="h-4 w-4" />
                    Do's
                  </h4>
                  <div className="space-y-2">
                    {content.dos.map((item, i) => (
                      <div key={i} className="flex items-start gap-2 text-sm p-2 rounded bg-emerald-50 dark:bg-emerald-950/30">
                        <CheckCircle className="h-4 w-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                        <span>{item}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Don'ts */}
                <div>
                  <h4 className="font-medium flex items-center gap-2 mb-3 text-red-600">
                    <XCircle className="h-4 w-4" />
                    Don'ts
                  </h4>
                  <div className="space-y-2">
                    {content.donts.map((item, i) => (
                      <div key={i} className="flex items-start gap-2 text-sm p-2 rounded bg-red-50 dark:bg-red-950/30">
                        <XCircle className="h-4 w-4 text-red-500 mt-0.5 flex-shrink-0" />
                        <span>{item}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {step === 2 && (
              <div className="space-y-4">
                {/* Tips */}
                <div>
                  <h4 className="font-medium flex items-center gap-2 mb-3 text-amber-600">
                    <Lightbulb className="h-4 w-4" />
                    Pro Tips
                  </h4>
                  <div className="space-y-2">
                    {content.tips.map((tip, i) => (
                      <div key={i} className="flex items-start gap-2 text-sm p-3 rounded bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800">
                        <Sparkles className="h-4 w-4 text-amber-500 mt-0.5 flex-shrink-0" />
                        <span>{tip}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Voice Mode Notice */}
                <div className="p-3 rounded-lg bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800">
                  <div className="flex items-start gap-2">
                    <Mic className="h-4 w-4 text-blue-500 mt-0.5" />
                    <div className="text-sm">
                      <p className="font-medium text-blue-700 dark:text-blue-300">Voice Mode Active</p>
                      <p className="text-blue-600 dark:text-blue-400">
                        Speak naturally. Your microphone will be ready when you start.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Warning about external help */}
                <div className="p-3 rounded-lg bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800">
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="h-4 w-4 text-amber-500 mt-0.5" />
                    <div className="text-sm">
                      <p className="font-medium text-amber-700 dark:text-amber-300">Integrity Notice</p>
                      <p className="text-amber-600 dark:text-amber-400">
                        Responses are analyzed for naturalness. Speak from your knowledge, not from external sources.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </ScrollArea>
        </div>

        <DialogFooter className="flex-col sm:flex-row gap-3">
          {showDontShowAgain && (
            <div className="flex items-center gap-2 mr-auto">
              <Checkbox
                id="dont-show"
                checked={dontShowAgain}
                onCheckedChange={setDontShowAgain}
              />
              <label htmlFor="dont-show" className="text-sm text-muted-foreground cursor-pointer">
                Don't show this again
              </label>
            </div>
          )}
          
          <div className="flex gap-2">
            {step > 0 && (
              <Button variant="outline" onClick={() => setStep(step - 1)}>
                Back
              </Button>
            )}
            {step < 2 ? (
              <Button onClick={() => setStep(step + 1)}>
                Next
                <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            ) : (
              <Button 
                onClick={handleStart}
                className={`bg-gradient-to-r ${content.color}`}
                data-testid="start-session-btn"
              >
                <Sparkles className="h-4 w-4 mr-2" />
                Start Session
              </Button>
            )}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export { SessionGuidanceModal, GUIDANCE_CONTENT };
export default SessionGuidanceModal;
